import { createElement } from "react";
import type { BootstrapPayload, Command, MenuEntry } from "../../core";
import { command } from "../../core";
import { appIcon } from "../../managers/icons";
import { firstMenuAction } from "../../managers/workspace-utils";
import { fuzzyScore } from "../scoring";
import type { Candidate, CommandPaletteSource } from "../types";

/**
 * Navigate source — the single flat list shown in `/` (menus) mode.
 *
 * Replaces the old Apps + Menus + Actions trio with one breadcrumb-formatted
 * list, matching the canonical prototype:
 *   { app, app › section, app › section › leaf, … }
 *
 * Each entry's `path` is fed verbatim into `Candidate.crumb` so the UI can
 * render `app › section › leaf` with chevrons. The cmdk `value` is computed
 * from the same path so cmdk's filtering matches against the breadcrumb.
 */

interface NavEntry {
    appId: string;
    appName: string;
    appColorIndex?: number;
    appIconName?: string;
    path: string[];
    actionKey?: string;
}

function buildNavIndex(boot: BootstrapPayload): NavEntry[] {
    const out: NavEntry[] = [];

    for (const app of boot.apps) {
        // App root — pick the app's first navigable action as the landing.
        const landing = firstMenuAction(boot.menus[app.id] ?? []);
        out.push({
            appId: app.id,
            appName: app.name,
            appColorIndex: app.color,
            appIconName: app.icon,
            path: [app.name],
            actionKey: landing?.actionKey,
        });

        // DFS walk over the menu tree, emitting an entry for every node
        // (parent OR leaf). Each node's path is [app, ...ancestors, name].
        const walk = (entries: MenuEntry[], ancestors: string[]): void => {
            for (const entry of entries) {
                const actionKey = entry.actionKey ?? entry.action?.key;
                out.push({
                    appId: app.id,
                    appName: app.name,
                    appColorIndex: app.color,
                    appIconName: app.icon,
                    path: [app.name, ...ancestors, entry.name],
                    actionKey,
                });
                if (entry.children?.length) {
                    walk(entry.children, [...ancestors, entry.name]);
                }
            }
        };
        walk(boot.menus[app.id] ?? [], []);
    }

    return out;
}

export function buildNavigateSource(
    boot: BootstrapPayload,
    dispatch: (cmd: Command) => Promise<unknown>,
    getCurrentAppId?: () => string | undefined,
): CommandPaletteSource {
    const index = buildNavIndex(boot);

    return {
        key: "navigate",
        label: "Navigate to",
        prefix: "/",
        async search(query: string): Promise<Candidate[]> {
            const currentAppId = getCurrentAppId?.();
            const hasQuery = query.trim().length > 0;

            // Two regimes:
            //  - Empty query (initial /-mode landing) → CURRENT app's full
            //    menu tree, plus every OTHER app's root entry only. The user
            //    sees their workable surface up front + a one-line shortcut
            //    into every other app.
            //  - Non-empty query → search ACROSS every app's full tree so
            //    matches anywhere surface. Engine sorts by fuzzy score;
            //    current-app rows still win on score ties.
            const scope = hasQuery
                ? index.slice()
                : index.filter((entry) => entry.appId === currentAppId || entry.path.length === 1);
            scope.sort((a, b) => {
                const aCurrent = a.appId === currentAppId ? 0 : 1;
                const bCurrent = b.appId === currentAppId ? 0 : 1;
                if (aCurrent !== bCurrent) return aCurrent - bCurrent;
                return 0;
            });

            const out: Candidate[] = [];
            for (const entry of scope) {
                const flat = entry.path.join(" ");
                const score = fuzzyScore(query, flat, entry.path);
                if (score <= 0) continue;
                const leaf = entry.path[entry.path.length - 1];
                const Icon = appIcon(entry.appIconName);
                out.push({
                    id: `nav:${entry.appId}:${entry.path.join("/")}`,
                    title: leaf,
                    crumb: entry.path,
                    keywords: entry.path,
                    score,
                    accentColorIndex: entry.appColorIndex,
                    icon: createElement(Icon, { size: 11 }),
                    onPick: () => {
                        if (entry.actionKey) {
                            void dispatch(command("OPEN_ACTION", { actionKey: entry.actionKey }));
                        }
                        // Pure parent node without a landing → no-op.
                    },
                });
            }
            return out;
        },
    };
}
